import { Result as AntResult, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { FaLock, FaExclamationTriangle, FaHome, FaArrowLeft } from "react-icons/fa";

export type ResultType = "403" | "404" | "unauthorized" | "error";

interface ResultProps {
  type: ResultType;
  title?: string;
  subtitle?: string;
  extra?: React.ReactNode;
  showBackButton?: boolean;
  backTo?: string;
}

const resultConfig: Record<
  ResultType,
  {
    status: "403" | "404" | "error" | "warning";
    defaultTitle: string;
    defaultSubtitle: string;
    icon?: React.ReactNode;
  }
> = {
  "403": {
    status: "403",
    defaultTitle: "Acceso Denegado",
    defaultSubtitle: "No tienes los permisos necesarios para acceder a esta página.",
    icon: <FaLock className="text-4xl" />,
  },
  "404": {
    status: "404",
    defaultTitle: "Página No Encontrada",
    defaultSubtitle: "La página que buscas no existe o ha sido movida.",
    icon: <FaExclamationTriangle className="text-4xl" />,
  },
  unauthorized: {
    status: "error",
    defaultTitle: "No Autorizado",
    defaultSubtitle: "No tienes autorización para realizar esta acción.",
    icon: <FaLock className="text-4xl" />,
  },
  error: {
    status: "error",
    defaultTitle: "Error",
    defaultSubtitle: "Ha ocurrido un error inesperado.",
    icon: <FaExclamationTriangle className="text-4xl" />,
  },
};

export function Result({
  type,
  title,
  subtitle,
  extra,
  showBackButton = true,
  backTo,
}: ResultProps) {
  const navigate = useNavigate();
  const config = resultConfig[type];

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  const handleHome = () => {
    navigate("/dashboard");
  };

  const defaultExtra = (
    <div className="flex gap-3 justify-center">
      {showBackButton && (
        <Button
          type="default"
          icon={<FaArrowLeft />}
          onClick={handleBack}
          size="large"
        >
          Volver
        </Button>
      )}
      <Button
        type="primary"
        icon={<FaHome />}
        onClick={handleHome}
        size="large"
      >
        Ir al Inicio
      </Button>
    </div>
  );

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <AntResult
        status={config.status}
        title={title || config.defaultTitle}
        subTitle={subtitle || config.defaultSubtitle}
        icon={config.icon}
        extra={extra || defaultExtra}
      />
    </div>
  );
}
